import React, { FC, useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { FieldProps } from 'formik';
import { useTheme } from '@mui/material/styles';
import classNames from 'classnames';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import SortRoundedIcon from '@mui/icons-material/SortRounded';

import { InputLabel } from '@shared/components/forms/inputs/InputLabel';
import { DeviceIcon } from '@shared/components/DeviceIcon';
import { Tooltip } from '@mui/material';

import { useStyles, getCustomStyles } from './ProcedureAssignmentDropdown.styles';
import { ThemeProps } from '@styles/theme';

import { useProceduresUIStore, useUserUIStore } from '@core/useStores';
import { Button } from '@mui/material';

const ENTER_KEYCODE = "Enter";

export type ProcedureAssignmentDropdownProps = {
    className?: string,
    options: Array<object>,
    components?: object,
    closeMenuOnSelect?: boolean,
    hideSelectedOptions?: boolean,
    isMulti?: boolean,
    onChange?: (value: OptionType, fieldName: string) => void,
    label?: string;
    fieldName: string;
    placeholder?: string;
    isRequired?: boolean;
    isClearable?: boolean;
    isSearchable?: boolean;
    disabled?: boolean;
    error?: string;
}

export type OptionType = {
    value: string,
    label: string,
    type?: string
}

export type ProcedureOptions = {
    value: string,
    label: string,
    disabled: boolean,
    type?: string | null,
    platforms?: string[],
}

export type VersionValue = {
    value: {
        versionId: string|null,
        procedureId: string,
    },
    label: string,
    disabled: boolean,
    devices?: Array<{ platform: string | null, environments: string[] }>
}

export type VersionOptionType = {
    value: string|null,
    label: string,
    type?: string,
    devices?: Array<{ platform: string | null, environments: string[] }>,
    date?: string,
    languages?: string[],
    editorVersion?: string | null,
}

export type VersionOptions = {
    procedureId: string,
    options: VersionOptionType[]
}

export const ProcedureAssignmentDropdown: FC<ProcedureAssignmentDropdownProps & FieldProps> = ({
    className,
    label,
    fieldName,
    isRequired,
    meta,
    disabled,
    form,
    field,
    ...props
}) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const customStyles = getCustomStyles(theme.colors);

    const [proceduresOptions, setProceduresOptions] = useState<ProcedureOptions[]>([])

    const [versionsOptions, setVersionsOptions] = useState<VersionOptions[]>([])

    const [procedureToAssign, setProcedureToAssign] = useState<{ value: any; label: any; disabled: any; }>();

    const [procedureVersions, setProcedureVersions] = useState<{ value: any; label: any; disabled: any; }[]>([]);

    const [versionToAssign, setVersionToAssign] = useState<{ value: any; label: any; disabled: any; }>();

    const [reloadSelector, setReloadSelector] = useState<boolean>(false)

    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

    const [procedureMeta, setProcedureMeta] = useState<Map<string, { type: string | null; platforms: string[] }>>(new Map())

    const versionSelectRef = useRef<any>(null);

    const proceduresUIStore = useProceduresUIStore();
    const userUIStore = useUserUIStore();

    const LoadData = async () =>{
        proceduresUIStore.cleanUp();
        proceduresUIStore.togglePageActivity(true);
        proceduresUIStore.setParams({
            limit: 1000,
        })

        await proceduresUIStore.load();
        proceduresUIStore.loadStatistics();

        // map((o: any) => ({value: o.value, label: o.label, disabled: o.disabled}))
        const verOpt: VersionOptions[] = [];
        proceduresUIStore.procedures.forEach(p => {
            if(p){
                const versions = p.versions;
                const option: VersionOptionType[] = versions.map(v => ({
                    label: v.version,
                    value: v.id,
                    devices: v.devices.map(d => ({ platform: d.platform, environments: Array.from(d.environments) })),
                    date: v.date,
                    languages: Array.from(v.languages),
                    executionModes: Array.from(v.executionModes),
                    editorVersion: v.editorVersion ?? undefined,
                }));
                option.unshift({value: null, label: 'latest'});
                verOpt.push({procedureId: p.id, options: option});
            }
        })
        setVersionsOptions(verOpt);

        const meta = new Map<string, { type: string | null; platforms: string[] }>();
        proceduresUIStore.procedures.forEach(p => {
            const platforms: string[] = [];
            p.versions.forEach(v => {
                v.devices.forEach(d => {
                    if (d.platform && !platforms.includes(d.platform)) {
                        platforms.push(d.platform);
                    }
                });
            });
            meta.set(p.id, { type: p.type ?? null, platforms });
        });
        setProcedureMeta(meta);
    }

    useEffect(() => {
        LoadData();
    }, []);

    useEffect(() =>{
        const proOpt: ProcedureOptions[] = [];
        props.options.forEach((o : any) => {
            if(o){
                if(!field.value.find((p : any) => p.value.id == o.value)){
                    const meta = procedureMeta.get(o.value);
                    proOpt.push({
                        value: o.value,
                        label: o.label,
                        disabled: o.disabled,
                        type: meta?.type ?? null,
                        platforms: meta?.platforms ?? [],
                    });
                }
            }
        });
        proOpt.sort((a, b) => a.label.localeCompare(b.label));
        setProceduresOptions(proOpt);
    }, [field.value, procedureMeta])

    useEffect(() => {
        if (procedureToAssign) {
            versionSelectRef.current?.focus();
        }
    }, [procedureToAssign]);

    const handleChange = (value: any) => {
        const fieldValue = field.value;
        const procedureFieldValue = fieldValue.find((p: any) => p.value.id == value.value.procedureId);
        if(procedureFieldValue){
            const procedureIndex = fieldValue.indexOf(procedureFieldValue);
            fieldValue[procedureIndex] = {
                value:
                    {
                        id: value.value.procedureId,
                        version: value.value.versionId,
                    },
                label: procedureFieldValue.label,
            };
            if (props.onChange) { props.onChange(fieldValue, fieldName); }
            form.setFieldValue(fieldName, fieldValue);
        }
    }

    const removeProcedure = (procedureId: string) => {
        const value = field.value;
        const newValue = value.filter((p: any) => p.value.id != procedureId);
        if (props.onChange) { props.onChange(newValue, fieldName); }
        form.setFieldValue(fieldName, newValue);
    }

    const handleProcedureSet = (value: any) =>{
        setProcedureToAssign(value);
        setVersionToAssign(undefined);

        const versionOptions = versionsOptions.find(p => p.procedureId == value.value)

        if(versionOptions){
            const options = versionOptions?.options.map((o: any) => ({ ...o, disabled: !!o.disabled }));

            const procedurePresent = field.value.find((p: any) => p.value.id == value.value);
            if(procedurePresent){
                const availableOptions = options.filter((o: any) => o.value != procedurePresent.value.version);

                setProcedureVersions(availableOptions);
            }else{
                setProcedureVersions(options);
            }
        }
    }
    const handleVersionSet = (value: any) =>{
        setVersionToAssign({value: value.value, label: value.label, disabled: false})
    }

    const assignProcedure = () =>{
        if(procedureToAssign){
            const newValue: any =  Array.from(field.value);
            newValue.push(
                {
                    value:
                    {
                        id: procedureToAssign.value,
                        version: versionToAssign ? versionToAssign.value : null,
                    },
                    label: procedureToAssign.label,
                });
            if (props.onChange) { props.onChange(newValue, fieldName); }
            form.setFieldValue(fieldName, newValue);
            setVersionToAssign(undefined);
            setProcedureToAssign(undefined);
            setReloadSelector(true);
            setTimeout(() => {
                setReloadSelector(false);
            }, 100);
        }
    }

    const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.code === ENTER_KEYCODE) {
            e.preventDefault();
            form.submitForm();
        }
    }

    const handleBlur = () =>
        form.setFieldTouched(fieldName, true);

    const filterOption = (option: OptionType, inputValue: string) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase());

    const filterProcedureOption = (option: { data: ProcedureOptions }, inputValue: string) => {
        if (!inputValue) {return true;}
        const q = inputValue.toLowerCase();
        const { label, type, platforms } = option.data;

        return (
            label.toLowerCase().includes(q) ||
            (!!type && type.toLowerCase().includes(q)) ||
            (!!platforms && platforms.some(p => p.toLowerCase().includes(q)))
        );
    };

    const getTypeChipClass = (type: string | null | undefined) => {
        if (!type) {return styles.procedureTypeChipDefault;}
        switch (type) {
                        case 'VT':  return styles.procedureTypeChipVT;
                        case 'OpS': return styles.procedureTypeChipOpS;
                        default:    return styles.procedureTypeChipDefault;
        }
    };

    const PLATFORM_ICONS: Record<string, string> = {
        windows:          require('@assets/pc.svg'),
        'windows-screen2d': require('@assets/pc.svg'),
        'windows-vr':     require('@assets/pc-vr.svg'),
        osx:              require('@assets/pc.svg'),
        'osx-screen2d':   require('@assets/pc.svg'),
        'osx-vr':         require('@assets/pc-vr.svg'),
        android:          require('@assets/android.svg'),
        'android-screen2d': require('@assets/android.svg'),
        'android-ar':     require('@assets/android-ar.svg'),
        'android-vr':     require('@assets/android-vr.svg'),
        ios:              require('@assets/ios.svg'),
        'ios-screen2d':   require('@assets/ios.svg'),
        'ios-ar':         require('@assets/ios-ar.svg'),
        'ios-vr':         require('@assets/ios-vr.svg'),
        pc:               require('@assets/pc.svg'),
        'pc-vr':          require('@assets/pc-vr.svg'),
        VR:               require('@assets/vr.svg'),
        AR:               require('@assets/ar.svg'),
        Mobile:           require('@assets/tablet.svg'),
        PC:               require('@assets/pc.svg'),
    };

    const formatProcedureOptionLabel = (option: ProcedureOptions) => (
        <div className={styles.procedureOption}>
            <span className={styles.procedureOptionName}>{option.label}</span>
            <div className={styles.procedureOptionMeta}>
                {option.type && (
                    <span className={classNames(styles.procedureTypeChip, getTypeChipClass(option.type))}>
                        {option.type}
                    </span>
                )}
                {option.platforms && option.platforms.length > 0 && option.platforms.map((platform, i) => {
                    const src = PLATFORM_ICONS[platform] ?? PLATFORM_ICONS[platform.toLowerCase()];
                    if (!src) {return null;}

                    return (
                        <Tooltip key={i} title={platform} arrow>
                            <img
                                src={src}
                                alt={platform}
                                className={styles.procedurePlatformIcon}
                            />
                        </Tooltip>
                    );
                })}
            </div>
        </div>
    );

    const formatVersionOptionLabel = (option: any, { context }: { context: string }) => {
        if (context === 'value') {
            return <span>{option.label}</span>;
        }
        const dateStr = option.date
            ? new Date(option.date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
            : null;
        const hasExtra = dateStr || option.languages?.length || option.devices?.length || option.editorVersion;

        return (
            <div className={styles.versionOption}>
                <span className={styles.versionOptionLabel}>{option.label}</span>
                {hasExtra && (
                    <div className={styles.versionMeta}>
                        {dateStr && <span className={styles.versionDate}>{dateStr}</span>}
                        {option.editorVersion && (
                            <span className={styles.versionMetaChip}>{option.editorVersion}</span>
                        )}
                        {option.languages?.length > 0 && option.languages.map((lang: string, i: number) => (
                            <span key={i} className={styles.versionMetaChip}>{lang.toUpperCase()}</span>
                        ))}
                        {option.devices?.length > 0 && option.devices.map((d: any, i: number) => d.platform && (
                            <DeviceIcon
                                key={i}
                                platform={d.platform}
                                environments={d.environments?.join('|') || null}
                                size="small"
                                showLabel={false}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const sortedAssignedProcedures = [...field.value].sort((a: any, b: any) => {
        const cmp = a.label.localeCompare(b.label);

        return sortOrder === 'asc' ? cmp : -cmp;
    });

    return (
        <div className={styles.wrapper}>
            {
                label && (
                    <InputLabel
                        label={label}
                        isRequired={isRequired}
                        className={classNames(
                            { [styles.labelWithError]: meta.touched && meta.error }
                        )}
                    />
                )
            }
            <div>
                {field.value.length > 0 && (
                    <div className={styles.procedureListHeader}>
                        <span className={styles.assignedCount}>
                            {field.value.length} assigned
                        </span>
                        <button
                            type="button"
                            className={classNames(styles.sortBtn, sortOrder === 'desc' && styles.sortBtnActive)}
                            onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
                            title={sortOrder === 'asc' ? 'Sorted A–Z, click for Z–A' : 'Sorted Z–A, click for A–Z'}
                        >
                            <SortRoundedIcon />
                            {sortOrder === 'asc' ? 'A–Z' : 'Z–A'}
                        </button>
                    </div>
                )}
                {sortedAssignedProcedures.map((pro: any) =>{

                    const versionOptions = versionsOptions.find(p => p.procedureId == pro.value.id);

                    const version = versionOptions?.options.find(v => v.value == pro.value.version);

                    const options = versionOptions?.options.map((o: any) => ({
                        value: { versionId: o.value, procedureId: pro.value.id },
                        label: o.label,
                        disabled: o.disabled,
                        devices: o.devices,
                        date: o.date,
                        languages: o.languages,
                        editorVersion: o.editorVersion,
                    }));

                    const versionValue: VersionValue = {value: { versionId: version?.value ? version?.value : null, procedureId: pro.value.id}, label: version?.label ? version?.label : 'latest', disabled: false, devices: version?.devices ?? []};

                    const procedure = proceduresUIStore.procedures.find(p => p.id == pro.value.id);

                    const isAssigned = !!procedure?.users.find(u => u.id == userUIStore.userToEditId);
                    const previewSrc = procedure?.procedurePreview?.src;

                    return (
                        <div key={pro.value.id} className={classNames(isAssigned ? styles.assignedProcedureContainer : styles.unassignedProcedureContainer, className)}>
                            <div className={classNames(styles.procedureLabelContainer, className)}>
                                <div className={styles.procedureThumb}>
                                    {previewSrc
                                        ? <img src={previewSrc} alt="" />
                                        : <DescriptionIcon fontSize="small" />
                                    }
                                </div>
                                <span className={styles.procedureLabel} title={pro.label}>
                                    {pro.label}
                                </span>
                            </div>
                            { version &&
                                <Select
                                    className={classNames(styles.select, className)}
                                    isDisabled={disabled}
                                    {...props}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    styles={customStyles}
                                    defaultValue={versionValue}
                                    filterOption={filterOption}
                                    onKeyDown={onKeyDown}
                                    isMulti={false}
                                    closeMenuOnSelect={true}
                                    options={options}
                                    formatOptionLabel={formatVersionOptionLabel}
                                />
                            }
                            <button
                                type="button"
                                className={styles.removeBtn}
                                onClick={() => removeProcedure(pro.value.id)}
                                title="Remove assignment"
                            >
                                <CloseIcon />
                            </button>
                        </div>
                    )
                })}
                <div className={styles.assignRow}>
                    {!reloadSelector &&
                        <>
                            <Select
                                className={classNames(styles.select, className)}
                                isDisabled={false}
                                // getOptionLabel={option => option.name}
                                {...props}
                                isSearchable={true}
                                placeholder={'Search by name, type or platform…'}
                                value={procedureToAssign}
                                onChange={handleProcedureSet}
                                onBlur={handleBlur}
                                styles={customStyles}
                                filterOption={filterProcedureOption}
                                onKeyDown={onKeyDown}
                                isMulti={false}
                                closeMenuOnSelect={true}
                                options={proceduresOptions}
                                formatOptionLabel={formatProcedureOptionLabel}
                            />
                            <Select
                                className={classNames(styles.select, className)}
                                isDisabled={procedureToAssign ? false : true}
                                // getOptionLabel={option => option.name}
                                {...props}
                                placeholder={'select version...'}
                                value={versionToAssign ?? null}
                                onChange={handleVersionSet}
                                onBlur={handleBlur}
                                styles={customStyles}
                                filterOption={filterOption}
                                onKeyDown={onKeyDown}
                                isMulti={false}
                                closeMenuOnSelect={true}
                                openMenuOnFocus={true}
                                ref={versionSelectRef}
                                options={procedureVersions}
                                formatOptionLabel={formatVersionOptionLabel}
                            />
                            <div className={styles.buttonContainer}>
                                <Button
                                    onClick={assignProcedure}
                                    className={styles.button}
                                    // color={!assigningProcedure ? 'secondary' : 'primary'}
                                >
                                    {'Assign'}
                                </Button>
                            </div>
                        </>
                    }
                </div>
            </div>
        </div>
    )
}
